/**
 * Phase C Workstream 3 — Playbook Explorer Zustand store.
 *
 * Backs the /playbooks list and /playbooks/:playbookId detail pages.
 * The store is a thin cache around `creativeMemoryApi`: list + detail
 * are fetched on demand, admin actions (archive, validate, trigger
 * cycle) call the API and then re-fetch the detail to reflect the
 * mutation in the UI immediately.
 */

import { create } from 'zustand';
import {
  creativeMemoryApi,
  type PlaybookListItem,
  type PlaybookDetail,
  type RuleAdminResponse,
} from '@/services/api';

interface PlaybooksState {
  // List
  list: PlaybookListItem[];
  listLoading: boolean;
  listError: string | null;

  // Detail (only one playbook held at a time — the page that mounts
  // this store is the detail page, and it loads the bundle the user
  // is currently viewing)
  detail: PlaybookDetail | null;
  detailLoading: boolean;
  detailError: string | null;

  // Wiki (markdown text; fetched lazily so the detail JSON stays
  // small and the wiki tab can show a spinner independently)
  wiki: string | null;
  wikiLoading: boolean;
  wikiError: string | null;

  // Admin action state — for disabling buttons during in-flight
  // archive/validate calls
  pendingRuleAction: string | null;
  triggerInFlight: boolean;
  lastActionMessage: string | null;

  // Actions
  fetchList: () => Promise<void>;
  fetchDetail: (playbookId: string) => Promise<void>;
  fetchWiki: (playbookId: string) => Promise<void>;
  archiveRule: (
    playbookId: string,
    ruleId: string,
    note?: string,
  ) => Promise<RuleAdminResponse | null>;
  validateRule: (
    playbookId: string,
    ruleId: string,
    note?: string,
  ) => Promise<RuleAdminResponse | null>;
  triggerCycle: (playbookId: string) => Promise<string | null>;
  clearDetail: () => void;
}

export const usePlaybooksStore = create<PlaybooksState>((set, get) => ({
  list: [],
  listLoading: false,
  listError: null,

  detail: null,
  detailLoading: false,
  detailError: null,

  wiki: null,
  wikiLoading: false,
  wikiError: null,

  pendingRuleAction: null,
  triggerInFlight: false,
  lastActionMessage: null,

  fetchList: async () => {
    set({ listLoading: true, listError: null });
    try {
      const resp = await creativeMemoryApi.listPlaybooks();
      set({ list: resp.items, listLoading: false });
    } catch (err) {
      set({
        listLoading: false,
        listError: err instanceof Error ? err.message : 'Failed to load playbooks',
      });
    }
  },

  fetchDetail: async (playbookId) => {
    set({ detailLoading: true, detailError: null });
    try {
      const detail = await creativeMemoryApi.getPlaybook(playbookId);
      set({ detail, detailLoading: false });
    } catch (err) {
      set({
        detailLoading: false,
        detailError:
          err instanceof Error ? err.message : 'Failed to load playbook detail',
      });
    }
  },

  fetchWiki: async (playbookId) => {
    set({ wikiLoading: true, wikiError: null });
    try {
      const text = await creativeMemoryApi.getWikiMarkdown(playbookId);
      set({ wiki: text, wikiLoading: false });
    } catch (err) {
      set({
        wikiLoading: false,
        wikiError: err instanceof Error ? err.message : 'Failed to load wiki',
      });
    }
  },

  archiveRule: async (playbookId, ruleId, note) => {
    set({ pendingRuleAction: ruleId, lastActionMessage: null });
    try {
      const resp = await creativeMemoryApi.archiveRule(playbookId, ruleId, {
        actor: 'admin_ui',
        note: note ?? null,
      });
      // Re-fetch the detail so the UI reflects the new rule state.
      await get().fetchDetail(playbookId);
      set({
        pendingRuleAction: null,
        lastActionMessage: `Archived rule ${ruleId}`,
      });
      return resp;
    } catch (err) {
      set({
        pendingRuleAction: null,
        lastActionMessage:
          err instanceof Error
            ? `Archive failed: ${err.message}`
            : 'Archive failed',
      });
      return null;
    }
  },

  validateRule: async (playbookId, ruleId, note) => {
    set({ pendingRuleAction: ruleId, lastActionMessage: null });
    try {
      const resp = await creativeMemoryApi.validateRule(playbookId, ruleId, {
        actor: 'admin_ui',
        note: note ?? null,
      });
      await get().fetchDetail(playbookId);
      set({
        pendingRuleAction: null,
        lastActionMessage: `Validated rule ${ruleId} (confidence ${resp.confidence.toFixed(2)})`,
      });
      return resp;
    } catch (err) {
      set({
        pendingRuleAction: null,
        lastActionMessage:
          err instanceof Error
            ? `Validate failed: ${err.message}`
            : 'Validate failed',
      });
      return null;
    }
  },

  triggerCycle: async (playbookId) => {
    set({ triggerInFlight: true, lastActionMessage: null });
    try {
      const resp = await creativeMemoryApi.triggerCycle(playbookId);
      set({
        triggerInFlight: false,
        lastActionMessage: `Cycle scheduled (run ${resp.run_id}). New bundle in ~10-30s.`,
      });
      return resp.run_id;
    } catch (err) {
      set({
        triggerInFlight: false,
        lastActionMessage:
          err instanceof Error
            ? `Cycle trigger failed: ${err.message}`
            : 'Cycle trigger failed',
      });
      return null;
    }
  },

  clearDetail: () => set({ detail: null, wiki: null, detailError: null }),
}));
